import { useMemo } from 'react';
import { Box } from '@mui/material';
import { CampaignChannels } from 'domains/campaigns/types';
import useFormatSmsContent from 'pages/ContentSmsBuilderPage/hooks/useFormatSmsContent';
import { getFormattedSmsAutoResponses } from 'pages/ContentSmsBuilderPage/components/SmsPreview/utils';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent/EmailPreviewContent';
import SmsPreview from 'pages/ContentSmsBuilderPage/components/SmsPreview';
import VoicePreviewContent from 'domains/content/components/VoicePreviewContent';
import DirectMailPreviewContent from 'domains/content/components/DirectMailPreviewContent/DirectMailPreviewContent';
import WhatsAppPreview from 'pages/ContentWhatsAppBuilderPage/components/WhatsAppPreview';
import useFormatWhatsAppContent from 'pages/ContentWhatsAppBuilderPage/hooks/useFormatWhatsAppContent';
import { getBodyComponentText } from 'pages/ContentWhatsAppBuilderPage/utils/whatsAppUtils';
import { isTemplate, Template, DirectMailSenderProfile, SenderProfile, WhatsApp } from 'domains/content/types';
import useContactColumns from 'hooks/queries/useContactColumns';
import { getMergeTags } from 'utils';

const outerBoxStyles = {
    width: '100%',
    flex: 1, // Take all remaining space
    minHeight: 0, // Allow shrinking below content size
    backgroundColor: 'grey.200',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden', // Hide overflow - inner content will handle scrolling
    margin: '32px 0',
    flexShrink: 0, // Prevent shrinking
};

const getInnerBoxStyles = (channel: CampaignChannels) => {
    const baseStyles = {
        width: '52%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '96%', // Always fill the outer box height
    };

    // For Email, Voice, and Direct Mail, expand to available height
    if (
        channel === CampaignChannels.EMAIL ||
        channel === CampaignChannels.VOICE_CALL ||
        channel === CampaignChannels.DIRECT_MAIL
    ) {
        return {
            ...baseStyles,
            overflow: 'hidden', // Hide overflow - preview components handle their own scrolling
        };
    }

    // For SMS and WhatsApp, center it (natural size)
    return {
        ...baseStyles,
        alignItems: 'center', // Center vertically
        justifyContent: 'center', // Center horizontally
    };
};

type Props = {
    channel: CampaignChannels;
    previewTemplate?: Template | object;
    emailSenderProfile?: SenderProfile;
    smsData?: { content?: string; autoResponses?: any[]; name?: string };
    mergeTags?: any;
    voiceData?: { voiceJson?: any; name?: string };
    previewDirectMail?: { html?: string; name?: string };
    directMailSenderProfile?: DirectMailSenderProfile;
    whatsAppData?: WhatsApp;
};

const ContentPreviewScreen = ({
    channel,
    previewTemplate,
    emailSenderProfile,
    smsData,
    mergeTags,
    voiceData,
    previewDirectMail,
    directMailSenderProfile,
    whatsAppData,
}: Props) => {
    // Fetch contact columns for WhatsApp merge tags preview (similar to SMS)
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns({
        enabled: channel === CampaignChannels.WHATSAPP,
    });

    // Create merge tags from contact columns for WhatsApp preview
    const whatsAppMergeTags = useMemo(() => {
        if (isContactColumnsSuccess && contactColumns) {
            return getMergeTags(contactColumns);
        }
        return [];
    }, [contactColumns, isContactColumnsSuccess]);

    const formattedSmsPreviewContent = useFormatSmsContent(smsData?.content, mergeTags);
    const formattedSmsAutoResponses = getFormattedSmsAutoResponses(smsData?.autoResponses, mergeTags);
    // Pass templateFields to transform numeric placeholders ({{1}}) to merge tag names ({{first_name}})
    const whatsAppContent = whatsAppData ? getBodyComponentText(whatsAppData, whatsAppData.templateFields) : '';
    // Use whatsAppMergeTags to show preview values (e.g., "Taylor" instead of "{{first_name}}")
    const formattedWhatsAppContent = useFormatWhatsAppContent(whatsAppContent, whatsAppMergeTags);

    // Render preview based on channel using switch
    const renderPreview = () => {
        switch (channel) {
            case CampaignChannels.EMAIL:
                return (
                    <EmailPreviewContent
                        emailTemplateHTML={isTemplate(previewTemplate) && previewTemplate.html}
                        fromAddress={emailSenderProfile?.from_address}
                        isGridItem
                        isOpen={true}
                        isCloseEnabled={false}
                        onClose={() => {}}
                        previewLabel=""
                    />
                );

            case CampaignChannels.SMS:
                return (
                    <SmsPreview
                        formattedContent={formattedSmsPreviewContent}
                        autoResponses={formattedSmsAutoResponses}
                    />
                );

            case CampaignChannels.VOICE_CALL:
                return (
                    <VoicePreviewContent
                        isOpen={true}
                        isCloseEnabled={false}
                        onClose={() => {}}
                        voiceJson={voiceData?.voiceJson}
                        previewLabel=""
                    />
                );

            case CampaignChannels.DIRECT_MAIL:
                return (
                    <DirectMailPreviewContent
                        isOpen={true}
                        htmlContent={previewDirectMail?.html}
                        isGridItem
                        isCloseEnabled={false}
                        onClose={() => {}}
                        directMailSender={directMailSenderProfile}
                        previewLabel=""
                    />
                );

            case CampaignChannels.WHATSAPP:
                return <WhatsAppPreview formattedContent={formattedWhatsAppContent} />;
        }
    };

    return (
        <Box sx={outerBoxStyles}>
            <Box sx={getInnerBoxStyles(channel)}>{renderPreview()}</Box>
        </Box>
    );
};

export default ContentPreviewScreen;
